import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Formik } from 'formik';
import ReactDOMServer from 'react-dom/server';
import { AppFileUploader, AppModal, AppRemixIcon } from '@/layout/AppComponents';
import errorHandler from '@/layout/components/error/ErrorHandler';
import AuxSidebar from '@/layout/Sidebar/AuxSidebar';
import LoopModal from '@/layout/components/helpers/LoopModal';
import Translator from '@/layout/components/helpers/Translator';
import useIsMounted from '@/layout/components/hooks/useIsMounted';
import { toFormData } from '@/minions/forms';
import { axiosPath, path } from '@/routing/helpers/router';
import * as currentPageAction from '@/store/actions/currentPageAction';

export default function GlobalImportSideBarBlock() {
    const { moduleBridgeHex, filtersHex, page } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isMounted = useIsMounted();
    const auxSidebar = useSelector(
        (state) => state.currentPage?.auxSidebar?.opened,
    );
    const [openOnce, setOpenOnce] = useState(false);
    const [downloadValues, setDownloadValues] = useState();
    const [importValues, setImportValues] = useState();
    const [errorMessage, setErrorMessage] = useState();

    const handleUploadWorkXls = useCallback(
        (values) =>
            axios
                .post(
                    axiosPath('api_r_user_module_globalLegalCompliance_import_uploadWorkXls'),
                    toFormData({ ...values, moduleBridgeHex }),
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                )
                .then(({ data }) => {
                    if (isMounted.current) {
                        setImportValues({ moduleBridgeHex, salt: data.salt });
                    }
                })
                .catch((e) => {
                    if (isMounted.current) {
                        setErrorMessage(e.response?.data?.error || 'Error');
                    }
                    dispatch(errorHandler(e));
                }),
        [moduleBridgeHex, dispatch, isMounted],
    );

    const handleAuxSidebar = () => {
        if (!openOnce) {
            setOpenOnce(true);
        }
        if (auxSidebar !== 'importXls') {
            dispatch(currentPageAction.change('auxSidebar', { opened: 'importXls' }));
        } else {
            dispatch(currentPageAction.change('auxSidebar', { opened: null }));
        }
    };

    return (
        <>
            <li
                className={auxSidebar && auxSidebar === 'importXls' ? 'active' : ''}
                onClick={handleAuxSidebar}
            >
                <span className="sidebar-element">
                    <AppRemixIcon icon="upload-2" className="sidebar-icon" />{' '}
                    <span className="sb-title">
                        <Translator file="export" name="export.import.button" />
                    </span>
                </span>
                <AuxSidebar
                    title={<Translator file="user" name="user.import.title" />}
                    isOpen={auxSidebar && auxSidebar === 'importXls'}
                    width="350px"
                    position="left"
                    onClose={handleAuxSidebar}
                >
                    {openOnce && (
                        <>
                            <Formik
                                initialValues={{ file: null }}
                                validateOnChange={false}
                                validateOnBlur={false}
                                onSubmit={handleUploadWorkXls}
                            >
                                {({ setFieldValue, handleSubmit, values, isSubmitting }) => (
                                    <>
                                        <AppFileUploader
                                            id={1}
                                            placeholder={ReactDOMServer.renderToString(
                                                values.file ? (
                                                    <Translator file="general" name="one.file.selected" />
                                                ) : (
                                                    <Translator file="general" name="select.a.file" />
                                                )
                                            )}
                                            onChange={(opt) => setFieldValue('file', opt)}
                                            btnText={<Translator file="general" name="search..." />}
                                        />
                                        <span
                                            className={`btn btn-xs btn-block btn-outline-light mt-2 mb-4${isSubmitting || !values.file ? ' disabled' : ''
                                                }`}
                                            onClick={() =>
                                                isSubmitting || !values.file ? undefined : handleSubmit()
                                            }
                                        >
                                            <AppRemixIcon icon="file-excel" />{' '}
                                            <Translator file="export" name="export.import.button" />
                                        </span>
                                    </>
                                )}
                            </Formik>
                            <span
                                className="btn btn-xs btn-block btn-success"
                                onClick={() => setDownloadValues({ moduleBridgeHex, filtersHex })}
                            >
                                <AppRemixIcon icon="file-excel" />{' '}
                                <Translator file="export" name="export.xls.button" />
                            </span>
                        </>
                    )}
                </AuxSidebar>
            </li>
            {downloadValues && (
                <LoopModal
                    route="api_r_user_module_globalLegalCompliance_import_downloadWorkXls"
                    value={downloadValues}
                    title={<Translator file="export" name="export.xls.button" />}
                    onFinish={(results) => {
                        setDownloadValues(undefined);
                        if (results?.error) {
                            setErrorMessage(results.error);
                        } else if (results?.s3FileUrl) {
                            window.location = results.s3FileUrl;
                        }
                    }}
                    onError={() => setDownloadValues(undefined)}
                />
            )}
            {importValues && (
                <LoopModal
                    route="api_r_user_module_globalLegalCompliance_import_importWorkXls"
                    value={importValues}
                    title={
                        <Translator file="module-legalCompliance" name="importing.dots" />
                    }
                    confirmBtnText={ReactDOMServer.renderToString(
                        <Translator file="user" name="user.accept" />
                    )}
                    onFinish={(results) => {
                        setImportValues(undefined);
                        if (results?.error) {
                            setErrorMessage(results.error);
                            return;
                        }
                        if ((results?.failed ?? 0) > 10000) {
                            setErrorMessage(
                                'Se han superado los 10.000 errores. Debes corregir el Excel y empezar de nuevo la importación.',
                            );
                            return;
                        }
                        const resultKey = importValues.salt;
                        sessionStorage.setItem(
                            `ggclImportResult:${resultKey}`,
                            JSON.stringify(results),
                        );
                        dispatch(currentPageAction.change('auxSidebar', { opened: null }));
                        navigate(
                            `${path({
                                name: 'user_module_globalLegalCompliance_evaluation',
                                params: {
                                    moduleBridgeHex,
                                    page,
                                    filtersHex,
                                },
                            })}?importResult=${encodeURIComponent(resultKey)}`,
                        );
                    }}
                    onError={() => setImportValues(undefined)}
                />
            )}
            <AppModal
                show={!!errorMessage}
                type="danger"
                title={<Translator file="user" name="user.error" />}
                onClose={() => setErrorMessage(undefined)}
                onConfirm={() => setErrorMessage(undefined)}
                isConfirmBtn
                confirmBtnText={<Translator file="user" name="user.accept" />}
            >
                {errorMessage}
            </AppModal>
        </>
    );
}
